import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { useFetch } from "@/lib/useData";
import { api } from "@/lib/api";
import { Mail, Phone, Globe, MapPin, Send, CheckCircle2, AlertCircle, Calendar } from "lucide-react";
import { Toaster, toast } from "sonner";
import OpeningHours from "@/components/OpeningHours";

function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-teal">
      <span className="h-px w-6 bg-teal" />
      {children}
    </span>
  );
}

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  subject: "General enquiry",
  message: "",
};

export default function Contact() {
  const { data: page } = useFetch("/pages/contact");
  const { data: settings } = useFetch("/site-settings");
  const [params] = useSearchParams();
  const isBooking = params.get("booking") === "1";
  const queryService = params.get("service") || "";
  const queryDivision = params.get("division") || "";
  const initialSubject = isBooking
    ? "Booking request — please contact me to schedule"
    : params.get("subject") || "General enquiry";
  const [form, setForm] = useState({
    ...INITIAL,
    subject: initialSubject,
    service_interest: queryService,
    division_interest: queryDivision,
    message: isBooking
      ? "I would like to book a consultation. Please contact me to confirm a suitable time."
      : "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    // Re-sync subject if user navigates to ?booking=1 from another link without remounting.
    if (isBooking && form.subject !== "Booking request — please contact me to schedule") {
      setForm((f) => ({
        ...f,
        subject: "Booking request — please contact me to schedule",
        message: f.message || "I would like to book a consultation. Please contact me to confirm a suitable time.",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBooking]);

  if (!page) return <div className="min-h-screen" />;

  const baseSubjects = page.subjects || ["General enquiry"];
  const subjects = isBooking
    ? ["Booking request — please contact me to schedule", ...baseSubjects.filter((s) => s !== "Booking request — please contact me to schedule")]
    : baseSubjects;
  const c = settings?.contact || {};
  const has = (v) => v && String(v).trim().length > 0;

  const contactItems = [
    has(c.email) && { icon: Mail, label: "Email", value: c.email, href: `mailto:${c.email}` },
    has(c.phone) && { icon: Phone, label: "Phone", value: c.phone, href: `tel:${(c.phone || "").replace(/[\s\-()]/g, "")}` },
    has(c.website) && { icon: Globe, label: "Website", value: c.website, href: `https://${c.website}` },
    (has(c.address_line_1) || has(c.city)) && {
      icon: MapPin,
      label: "Address",
      value: [c.address_line_1, c.address_line_2, c.city, c.country].filter(has).join(", "),
    },
  ].filter(Boolean);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact-messages", {
        ...form,
        source: isBooking ? "booking" : "contact_form",
      });
      setSent(true);
      setForm({ ...INITIAL, subject: initialSubject });
      toast.dismiss();
      toast.success("Message received. We'll be in touch shortly.", { id: "contact-success" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.dismiss();
      toast.error(
        Array.isArray(detail)
          ? detail.map((d) => d.msg).join(", ")
          : detail || "Could not send. Please try again.",
        { id: "contact-error" }
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO title={page.seo?.title} description={page.seo?.description} path="/contact" />
      <Toaster richColors position="top-center" />

      <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-24 bg-navy text-white overflow-hidden" data-testid="contact-hero">
        <div className="img-grain absolute inset-0" />
        <div className="container relative">
          <Reveal><Eyebrow>{page.intro?.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h1 data-testid="contact-title" className="mt-6 font-display font-bold text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              {page.intro?.title}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/80">
              {page.intro?.body}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" data-testid="contact-body">
        <div className="container grid gap-12 lg:grid-cols-12">
          {/* FORM */}
          <div className="lg:col-span-7">
            {isBooking && (
              <div data-testid="contact-booking-banner" className="mb-6 rounded-2xl border border-teal/30 bg-teal/5 p-5 flex items-start gap-3">
                <Calendar className="h-5 w-5 text-teal shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-navy-800">Booking request</p>
                  <p className="text-sm text-navy-700/80">Your subject and message have been pre-filled for a booking. Send the form and we&apos;ll confirm a time within one business day.</p>
                </div>
              </div>
            )}
            <Reveal>
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="rounded-3xl bg-soft-gray border border-navy-50 p-8 lg:p-10"
                noValidate
              >
                <h2 className="font-display font-semibold text-2xl text-navy-800">
                  Send us a message
                </h2>
                <p className="mt-2 text-sm text-navy-700/70">
                  Required fields are marked. We respond within one business day.
                </p>

                <div className="mt-8 grid gap-5 md:grid-cols-2">
                  <Field label="Full name" required>
                    <input
                      data-testid="contact-name"
                      value={form.name}
                      onChange={update("name")}
                      required
                      minLength={2}
                      className={inputCls}
                      placeholder="Your full name"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      data-testid="contact-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      required
                      className={inputCls}
                      placeholder="you@email.com"
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      className={inputCls}
                      placeholder="+252 ..."
                    />
                  </Field>
                  <Field label="Organisation">
                    <input
                      data-testid="contact-organization"
                      value={form.organization}
                      onChange={update("organization")}
                      className={inputCls}
                      placeholder="Company / institution"
                    />
                  </Field>
                  <Field label="Subject" required className="md:col-span-2">
                    <select
                      data-testid="contact-subject"
                      value={form.subject}
                      onChange={update("subject")}
                      className={inputCls}
                    >
                      {subjects.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Message" required className="md:col-span-2">
                    <textarea
                      data-testid="contact-message"
                      value={form.message}
                      onChange={update("message")}
                      required
                      minLength={10}
                      rows={6}
                      className={`${inputCls} resize-y min-h-[140px]`}
                      placeholder="How can we help?"
                    />
                  </Field>
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Send className="h-4 w-4" />
                    {busy ? "Sending..." : "Send message"}
                  </button>
                  {sent && (
                    <span className="inline-flex items-center gap-2 text-sm text-teal font-medium" data-testid="contact-success">
                      <CheckCircle2 className="h-4 w-4" /> Message sent
                    </span>
                  )}
                </div>
              </form>
            </Reveal>
          </div>

          {/* CONTACT INFO */}
          <aside className="lg:col-span-5">
            <Reveal delay={0.08}>
              <div className="rounded-3xl bg-navy text-white p-8 lg:p-10 relative overflow-hidden">
                <div className="img-grain absolute inset-0" />
                <div className="relative">
                  <h2 className="font-display font-semibold text-2xl">
                    Contact details
                  </h2>
                  {contactItems.length > 0 ? (
                    <ul className="mt-8 space-y-5">
                      {contactItems.map((it) => (
                        <li key={it.label} className="flex items-start gap-4">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-teal-light shrink-0">
                            <it.icon className="h-4 w-4" />
                          </span>
                          <div>
                            <p className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-semibold">
                              {it.label}
                            </p>
                            {it.href ? (
                              <a
                                href={it.href}
                                className="mt-1 block text-base font-medium text-white hover:text-teal-light transition-colors"
                                target={it.href.startsWith("http") ? "_blank" : undefined}
                                rel="noreferrer"
                              >
                                {it.value}
                              </a>
                            ) : (
                              <p className="mt-1 text-base font-medium text-white">{it.value}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-8 p-6 rounded-2xl bg-white/[0.04] border border-white/10">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-teal-light mt-0.5 shrink-0" />
                        <p className="text-sm text-white/80">
                          Visit our offices &mdash; details coming soon. In the meantime,
                          please use the form to reach our team.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-10 pt-8 border-t border-white/10">
                    <p className="text-xs uppercase tracking-[0.22em] text-teal-light font-semibold">
                      Care &middot; Compassion &middot; Commitment
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            {has(c.map_embed_url) && (
              <Reveal delay={0.16}>
                <div className="mt-6 aspect-[4/3] rounded-3xl overflow-hidden border border-navy-50">
                  <iframe
                    title="Map"
                    src={c.map_embed_url}
                    className="h-full w-full"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </Reveal>
            )}

            <Reveal delay={0.2}>
              <div className="mt-6">
                <OpeningHours hours={c.opening_hours} />
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full rounded-xl border border-navy-100 bg-white px-4 py-3 text-sm text-navy-800 placeholder:text-navy-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-colors";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">
        {label}
        {required && <span className="text-teal"> *</span>}
      </span>
      {children}
    </label>
  );
}
